import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import styled from "styled-components"
import { useTranslate } from "~/ur-react-hooks"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import SettingsAdminBilling from "./SettingsAdminBilling"
import SettingsChangePassword from "./SettingsChangePassword"
import SettingsInfo from "./SettingsInfo"

const Layout = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 1.5rem;
`

const Card = styled.div`
  width: 392px;
  height: auto;
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  border-radius: 8px;
`

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid ${(props) => props.theme.colors.primary99};
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`

interface TabProps {
  isActive: boolean
}

const Tab = styled.button<TabProps>`
  outline: none;
  flex: 1;
  text-align: center;
  padding: 14px;
  cursor: pointer;
  border: none;

  font-size: 14px;
  font-weight: 500;
  background: ${(props) =>
    props.isActive ? props.theme.colors.white : props.theme.colors.primary99};

  color: ${(props) =>
    props.isActive ? props.theme.colors.primary40 : props.theme.colors.gray60};
`

const Body = styled.div`
  padding: 1rem 2rem;
`

type SettingsTab = "information" | "billing" | "change-password"

export type SettingsProps = {}

const Settings: React.FC<SettingsProps> = () => {
  const [search, setSearch] = useSearchParams()
  const tab = (search.get("tab") as SettingsTab) || "information"

  const translations = useTranslate({
    information: "settings.tabs.information",
    changePassword: "settings.tabs.change-password",
    billing: "settings.tabs.billing",
  })

  const organization = useOrganizationMembership()

  const isAdminOfAnOrganization = ["ADMIN", "OWNER"].includes(
    organization?.role ?? "",
  )

  const setTab = useCallback(
    (tab: SettingsTab) => {
      setSearch({ tab })
    },
    [setSearch],
  )

  return (
    <Layout>
      <Card>
        <Tabs>
          <Tab
            onClick={() => setTab("information")}
            isActive={tab === "information"}
          >
            {translations.information}
          </Tab>
          {isAdminOfAnOrganization && (
            <Tab isActive={tab === "billing"} onClick={() => setTab("billing")}>
              {translations.billing}
            </Tab>
          )}
          <Tab
            isActive={tab === "change-password"}
            onClick={() => setTab("change-password")}
          >
            {translations.changePassword}
          </Tab>
        </Tabs>
        <Body>
          {tab === "information" && <SettingsInfo />}
          {tab === "billing" && <SettingsAdminBilling />}
          {tab === "change-password" && <SettingsChangePassword />}
        </Body>
      </Card>
    </Layout>
  )
}

export default Settings
